import { useState } from 'react';
import { useUserRole } from '@/hooks/useUserRole';
import {
  useActionItems,
  ActionItem,
  ActionItemStatus,
  ActionReviewAction,
} from '@/hooks/useActionItems';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTenantMembers } from '@/hooks/useTenantMembers';
import {
  CheckCircle2,
  XCircle,
  Edit3,
  Calendar,
  Mail,
  UserCheck,
  ListChecks,
  Clock,
  Loader2,
  Plus,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';

const statusConfig: Record<ActionItemStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  suggested: { label: 'Suggested', variant: 'default' },
  approved: { label: 'Approved', variant: 'default' },
  rejected: { label: 'Rejected', variant: 'destructive' },
  modified: { label: 'Modified', variant: 'secondary' },
  completed: { label: 'Completed', variant: 'outline' },
};

const sourceIcon = (source: ActionItem['source']) => {
  switch (source) {
    case 'meeting':
      return <Calendar className="w-3.5 h-3.5" />;
    case 'email':
      return <Mail className="w-3.5 h-3.5" />;
    default:
      return <UserCheck className="w-3.5 h-3.5" />;
  }
};

interface ActionHubProps {
  onNavigateToGovernance?: () => void;
}

const UNASSIGNED = 'unassigned';

const ActionHub = ({ onNavigateToGovernance }: ActionHubProps) => {
  const { role } = useUserRole();
  const { items, loading, submitting, submitReview, createManual } = useActionItems();
  const { members: tenantMembers } = useTenantMembers();

  const [tab, setTab] = useState<ActionItemStatus | 'all'>('suggested');

  // Review dialog state
  const [selected, setSelected] = useState<ActionItem | null>(null);
  const [action, setAction] = useState<ActionReviewAction | null>(null);
  const [notes, setNotes] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editAssigneeId, setEditAssigneeId] = useState<string>(UNASSIGNED);
  const [editDueDate, setEditDueDate] = useState('');
  const [editProjectId, setEditProjectId] = useState('');

  // Add Action dialog state
  const [addOpen, setAddOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [newAssigneeId, setNewAssigneeId] = useState<string>(UNASSIGNED);

  const canReview = [
    'project_manager',
    'senior_project_manager',
    'program_manager',
    'pmo',
    'executive',
    'admin',
  ].includes(role || '');

  const openAction = (item: ActionItem, a: ActionReviewAction) => {
    setSelected(item);
    setAction(a);
    setNotes('');
    setEditTitle(item.title);
    setEditAssigneeId(item.assignee_id || UNASSIGNED);
    setEditDueDate(item.due_date || '');
    setEditProjectId(item.project_id || '');
  };

  const closeAction = () => {
    setSelected(null);
    setAction(null);
  };

  const handleSubmit = async () => {
    if (!selected || !action) return;

    const editedAssignee = editAssigneeId === UNASSIGNED
      ? null
      : tenantMembers.find((m) => m.user_id === editAssigneeId);

    const result = await submitReview({
      id: selected.id,
      currentStatus: selected.status,
      action,
      notes,
      edits:
        action === 'modify'
          ? {
              title: editTitle,
              assignee_id: editAssigneeId === UNASSIGNED ? null : editAssigneeId,
              assignee_name: editedAssignee ? (editedAssignee.display_name || editedAssignee.email) : null,
              due_date: editDueDate || null,
              project_id: editProjectId || null,
            }
          : undefined,
    });

    if (result.ok === false) {
      toast.error(result.error);
      return;
    }

    toast.success(action === 'modify' ? 'Action item approved' : 'Action item updated');
    closeAction();
  };

  const handleCreateManual = async () => {
    if (!newTitle.trim()) {
      toast.error('Title is required');
      return;
    }

    const newAssignee = newAssigneeId === UNASSIGNED
      ? undefined
      : tenantMembers.find((m) => m.user_id === newAssigneeId);

    const result = await createManual({
      title: newTitle.trim(),
      description: newDescription.trim() || undefined,
      due_date: newDueDate || undefined,
      assignee_id: newAssigneeId === UNASSIGNED ? undefined : newAssigneeId,
      assignee_name: newAssignee ? (newAssignee.display_name || newAssignee.email) : undefined,
    });

    if (result.ok === false) {
      toast.error(result.error);
      return;
    }

    toast.success('Action added');
    setAddOpen(false);
    setNewTitle('');
    setNewDescription('');
    setNewDueDate('');
    setNewAssigneeId(UNASSIGNED);
  };

  const filtered = tab === 'all' ? items : items.filter((i) => i.status === tab);

  // 'modified' is no longer a status any UI action writes (Modify now
  // resolves straight to 'approved' — see useActionItems' STATUS_BY_ACTION),
  // so it's not a tab/count users navigate to. Historical 'modified' rows,
  // if any, still surface under "All".
  const counts = {
    suggested: items.filter((i) => i.status === 'suggested').length,
    approved: items.filter((i) => i.status === 'approved').length,
    rejected: items.filter((i) => i.status === 'rejected').length,
    completed: items.filter((i) => i.status === 'completed').length,
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ListChecks className="w-5 h-5 text-primary" />
              Action Hub
            </CardTitle>
            <CardDescription>
              Actions extracted from meetings and email, plus manual entries — review before they
              become tracked work.
            </CardDescription>
            <button
              type="button"
              onClick={onNavigateToGovernance}
              className="mt-2 text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1 disabled:cursor-default disabled:hover:text-muted-foreground"
              disabled={!onNavigateToGovernance}
            >
              Looking for risk/governance actions? <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <Button size="sm" onClick={() => setAddOpen(true)} className="gap-1">
            <Plus className="w-4 h-4" /> Add Action
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={tab} onValueChange={(v) => setTab(v as ActionItemStatus | 'all')}>
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="suggested">Suggested ({counts.suggested})</TabsTrigger>
            <TabsTrigger value="approved">Approved ({counts.approved})</TabsTrigger>
            <TabsTrigger value="rejected">Rejected ({counts.rejected})</TabsTrigger>
            <TabsTrigger value="completed">Completed ({counts.completed})</TabsTrigger>
            <TabsTrigger value="all">All</TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className="mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <ListChecks className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No action items in this state.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map((item) => (
                  <div
                    key={item.id}
                    className="border border-border rounded-lg p-4 hover:bg-muted/40 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <Badge variant={statusConfig[item.status].variant}>
                            {statusConfig[item.status].label}
                          </Badge>
                          <Badge variant="outline" className="text-xs gap-1">
                            {sourceIcon(item.source)}
                            {item.source}
                          </Badge>
                          {item.project_id && (
                            <Badge variant="outline" className="text-xs">
                              Linked project
                            </Badge>
                          )}
                        </div>
                        <h4 className="font-medium text-foreground">{item.title}</h4>
                        {item.description && (
                          <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                        )}
                        <div className="text-xs text-muted-foreground mt-2 flex items-center gap-3 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(item.created_at).toLocaleString()}
                          </span>
                          {item.assignee_name && <span>Assignee: {item.assignee_name}</span>}
                          {item.due_date && <span>Due: {new Date(item.due_date).toLocaleDateString()}</span>}
                          {item.reviewed_at && <span>Reviewed {new Date(item.reviewed_at).toLocaleString()}</span>}
                        </div>
                      </div>
                    </div>

                    {item.review_notes && (
                      <div className="text-xs bg-muted/50 rounded px-2 py-1 mb-2">
                        <span className="font-medium">Reviewer note:</span> {item.review_notes}
                      </div>
                    )}

                    {canReview && item.status === 'suggested' && (
                      <div className="flex gap-2 flex-wrap pt-2 border-t border-border">
                        <Button size="sm" variant="default" onClick={() => openAction(item, 'approve')}>
                          <CheckCircle2 className="w-4 h-4 me-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openAction(item, 'modify')}>
                          <Edit3 className="w-4 h-4 me-1" /> Modify
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => openAction(item, 'reject')}>
                          <XCircle className="w-4 h-4 me-1" /> Reject
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Review dialog: approve / modify / reject */}
      <Dialog open={!!action} onOpenChange={(o) => { if (!o) closeAction(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {action === 'approve' && 'Approve Action Item'}
              {action === 'reject' && 'Reject Action Item'}
              {action === 'modify' && 'Modify & Approve Action Item'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm">
              <p className="font-medium">{selected?.title}</p>
              {selected?.description && <p className="text-muted-foreground">{selected.description}</p>}
            </div>

            {action === 'modify' && (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Title</label>
                  <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Assignee</label>
                  <Select value={editAssigneeId} onValueChange={setEditAssigneeId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                      {tenantMembers.map((m) => (
                        <SelectItem key={m.user_id} value={m.user_id}>
                          {m.display_name || m.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Due date</label>
                  <Input type="date" value={editDueDate ? editDueDate.slice(0, 10) : ''} onChange={(e) => setEditDueDate(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Project ID</label>
                  <Input value={editProjectId} onChange={(e) => setEditProjectId(e.target.value)} placeholder="Linked project id (optional)" />
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-medium text-muted-foreground">Notes</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={action === 'reject' ? 'Reason (required)' : 'Optional notes'}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeAction}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || (action === 'reject' && !notes.trim()) || (action === 'modify' && !editTitle.trim())}
              variant={action === 'reject' ? 'destructive' : 'default'}
            >
              {submitting && <Loader2 className="w-4 h-4 me-2 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Action dialog: manual entry, no review gate */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Action</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Title</label>
              <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="What needs to happen?" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Description</label>
              <Textarea value={newDescription} onChange={(e) => setNewDescription(e.target.value)} rows={3} placeholder="Optional details" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Due date</label>
              <Input type="date" value={newDueDate} onChange={(e) => setNewDueDate(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Assignee</label>
              <Select value={newAssigneeId} onValueChange={setNewAssigneeId}>
                <SelectTrigger>
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                  {tenantMembers.map((m) => (
                    <SelectItem key={m.user_id} value={m.user_id}>
                      {m.display_name || m.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateManual} disabled={submitting || !newTitle.trim()}>
              {submitting && <Loader2 className="w-4 h-4 me-2 animate-spin" />}
              Add Action
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ActionHub;
